import { useEffect, useState, ChangeEvent } from 'react';
import { IconTrash, InputText, PaymentType, Select } from '@ui/src';
import {
  ExternalAccountTopUpOptionFormValues,
  RoutingCodeKeyEnum,
} from '../../../hoc/common/external-accounts/types';
import { RoutingCodeTypes } from './types';

import cn from './styles.module.css';

const sortCodeRegexp = /^(\d){2}-(\d){2}-(\d){2}$/;

const RoutingCode = ({
  routingCodeValue,
  paymentType,
  formValues,
  setFormValues,
  setTopUpOptions,
  topUpOptions,
  topUpId,
  index,
}: RoutingCodeTypes) => {
  const [routingCode, setRoutingCode] = useState<{
    routingCodeKey: string;
    routingCodeValue: string;
  }>();
  const [sortCode, setSortCode] = useState('');
  const [sortCodeError, setSortCodeError] = useState('');

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!sortCodeRegexp.test(sortCode)) {
      setSortCodeError('Sort Code is incorrect');
    }

    if (sortCodeRegexp.test(sortCode) || !sortCode?.length) {
      setSortCodeError('');
    }

    let inputValue = sortCode;

    if (e.key !== 'Backspace') {
      const regex = /.{1,2}/g;

      inputValue = sortCode
        ?.replace(/[^0-9]/g, '')
        ?.match(regex)
        ?.join('-') as string;
    }

    setSortCode(inputValue);

    setRoutingCode({
      ...routingCode,
      routingCodeValue: inputValue,
    } as {
      routingCodeKey: string;
      routingCodeValue: string;
    });

    const nextFormValues = { ...formValues };

    nextFormValues.routingCodeEntries = nextFormValues?.routingCodeEntries?.map(
      (item) => {
        const result = { ...item };

        if (result?.id === routingCodeValue?.id) {
          result.routingCodeValue = inputValue;
          result.routingCodeKey = routingCode?.routingCodeKey as string;
        }

        return result;
      },
    );

    setFormValues(nextFormValues);

    const nextOptions = topUpOptions?.map((item) => {
      let result = { ...item };

      if (result.id === topUpId) {
        result = nextFormValues;
      }

      return result;
    });

    if (setTopUpOptions) {
      setTopUpOptions(nextOptions as ExternalAccountTopUpOptionFormValues[]);
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nextValues = { ...routingCode };
    const nextFormValues = { ...formValues };

    const { name, value } = e.target;

    nextFormValues.routingCodeEntries = nextFormValues?.routingCodeEntries?.map(
      (item) => {
        const result = { ...item };

        if (result?.id === routingCodeValue?.id) {
          if (name === `routingCodeKey-${routingCodeValue?.id}`) {
            result.routingCodeKey = value;
            result.routingCodeValue = nextValues.routingCodeValue as string;
            nextValues.routingCodeKey = value;
          } else if (name === `routingCodeValue-${routingCodeValue?.id}`) {
            result.routingCodeValue = value;
            nextValues.routingCodeValue = value;
            result.routingCodeKey = nextValues.routingCodeKey as string;
          }
        }

        return result;
      },
    );

    setRoutingCode(
      nextValues as {
        routingCodeKey: RoutingCodeKeyEnum;
        routingCodeValue: string;
      },
    );

    setFormValues(nextFormValues);

    const nextOptions = topUpOptions?.map((item) => {
      let result = { ...item };

      if (result.id === topUpId) {
        result = nextFormValues;
      }

      return result;
    });

    if (setTopUpOptions) {
      setTopUpOptions(nextOptions as ExternalAccountTopUpOptionFormValues[]);
    }
  };

  useEffect(() => {
    if (routingCodeValue) {
      setRoutingCode(routingCodeValue);
    }
  }, [routingCodeValue]);

  useEffect(() => {
    if (paymentType && routingCodeValue?.id?.includes('routingCode')) {
      if (paymentType === PaymentType.FEDWIRE) {
        setRoutingCode({
          ...routingCode,
          routingCodeKey: RoutingCodeKeyEnum.ABA,
        } as {
          routingCodeKey: string;
          routingCodeValue: string;
        });
      } else if (
        paymentType === PaymentType.BACS ||
        paymentType === PaymentType.FASTER_PAYMENT
      ) {
        setRoutingCode({
          ...routingCode,
          routingCodeKey: RoutingCodeKeyEnum.SORT_CODE,
        } as {
          routingCodeKey: string;
          routingCodeValue: string;
        });
      }
    }
  }, [paymentType, routingCodeValue]);

  let routingCodeTitle = 'Routing code';

  if (paymentType === PaymentType.FEDWIRE) {
    routingCodeTitle = 'ABA routing number';
  } else if (
    paymentType === PaymentType.BACS ||
    paymentType === PaymentType.FASTER_PAYMENT
  ) {
    routingCodeTitle = 'Sort code';
  }

  return (
    <div className={cn.routingCodeWrapper}>
      {paymentType === PaymentType.SWIFT && (
        <div className={`${cn.routingCodeFormGroup} ${cn.routingKeySelect}`}>
          <Select
            title="Routing code type"
            placeholder="Select type"
            name={`routingCodeKey-${routingCodeValue?.id}`}
            value={routingCode?.routingCodeKey}
            list={[
              {
                name: 'ABA routing number',
                value: RoutingCodeKeyEnum.ABA,
              },
              {
                name: 'BLZ Code',
                value: RoutingCodeKeyEnum.BLZ_CODE,
              },
              {
                name: 'Branch Code',
                value: RoutingCodeKeyEnum.BRANCH_CODE,
              },
              {
                name: 'BSB Code',
                value: RoutingCodeKeyEnum.BSB_CODE,
              },
              {
                name: 'Sort Code',
                value: RoutingCodeKeyEnum.SORT_CODE,
              },
              {
                name: 'IFS Code',
                value: RoutingCodeKeyEnum.IFS_CODE,
              },
              {
                name: 'Clabe Code',
                value: RoutingCodeKeyEnum.CLABE_CODE,
              },
              {
                name: 'CTN',
                value: RoutingCodeKeyEnum.CTN,
              },
            ]}
            onChange={(e) => {
              onChange({
                target: {
                  name: `routingCodeKey-${routingCodeValue?.id}`,
                  value: e.target.value as string,
                },
              } as ChangeEvent<HTMLInputElement>);

              if (!e.target.value) {
                setSortCode('');

                setRoutingCode({
                  routingCodeKey: '',
                  routingCodeValue: '',
                });

                const nextFormValues = { ...formValues };

                nextFormValues.routingCodeEntries =
                  nextFormValues?.routingCodeEntries?.map((item) => {
                    const result = { ...item };

                    if (result?.id === routingCodeValue?.id) {
                      result.routingCodeValue = '';
                    }

                    return result;
                  });

                setFormValues(nextFormValues);

                const nextOptions = topUpOptions?.map((item) => {
                  let result = { ...item };

                  if (result.id === topUpId) {
                    result = nextFormValues;
                  }

                  return result;
                });

                if (setTopUpOptions) {
                  setTopUpOptions(
                    nextOptions as ExternalAccountTopUpOptionFormValues[],
                  );
                }
              }
            }}
            withClearButton
          />
        </div>
      )}
      <div
        className={`${cn.routingCodeFormGroup} ${paymentType !== PaymentType.SWIFT ? cn.fullWidth : ''}`}
      >
        <InputText
          name={`routingCodeValue-${routingCodeValue?.id}`}
          title={routingCodeTitle}
          placeholder={`Enter ${routingCodeTitle}`}
          onChange={(e) => {
            if (routingCode?.routingCodeKey === RoutingCodeKeyEnum.SORT_CODE) {
              setSortCode(e.target.value);

              if (!e.target.value) {
                setSortCode('');
              }
            } else {
              onChange({
                target: {
                  name: `routingCodeValue-${routingCodeValue?.id}`,
                  value: e.target.value,
                },
              } as ChangeEvent<HTMLInputElement>);
            }
          }}
          maxLength={
            routingCode?.routingCodeKey === RoutingCodeKeyEnum.SORT_CODE
              ? 8
              : undefined
          }
          onKeyUp={(e) =>
            routingCode?.routingCodeKey === RoutingCodeKeyEnum.SORT_CODE
              ? onKeyUp(e)
              : undefined
          }
          error={sortCodeError}
          value={routingCode?.routingCodeValue}
          disabled={!routingCode?.routingCodeKey}
        />
      </div>
      {index !== 0 && (
        <button
          type="button"
          className={cn.deleteRoutingCodeButton}
          onClick={() => {
            const nextFormValues = { ...formValues };

            nextFormValues.routingCodeEntries =
              nextFormValues.routingCodeEntries?.filter(
                (item) => item.id !== routingCodeValue?.id,
              );

            const nextOptions = topUpOptions?.map((item) => {
              const result = { ...item };

              if (result.id === topUpId) {
                result.routingCodeEntries = nextFormValues.routingCodeEntries;
              }

              return result;
            });

            if (setTopUpOptions) {
              setTopUpOptions(
                nextOptions as ExternalAccountTopUpOptionFormValues[],
              );
            }

            setFormValues(nextFormValues);
          }}
        >
          <IconTrash />
        </button>
      )}
    </div>
  );
};

export default RoutingCode;
